import { addSeconds } from 'date-fns';
import humps from 'humps';
import decode from 'jwt-decode';
import { ErrorMessage } from '@/shared/domain/constants';
import { CreditasAffiliates } from '@/shared/domain/enums/Storage';
import type { SigninResponseSuccess } from '@/shared/infrastructure/clients';
import { getLogger } from '@/shared/infrastructure/datadog';

type DecodedSession = {
  userId: string;
  userType: string;
  username: string;
  source: string;
  tokenType: string;
  sessionId: string;
  exp: number;
  iat: number;
  iss: string;
};

type UserSession = {
  groupId: string;
  affiliateNameAlias: string;
  userId: string;
  name: string;
  fullName: string;
  socialName: string;
  email: string;
  loginDomain: string;
  products: string;
  type: string;
  cnpj: string;
};

type LoginSession = {
  user?: UserSession;
};

type SettingsSession = {
  showMobileWarning: boolean;
};

export type StateSession = {
  login?: LoginSession;
  settings?: SettingsSession;
};

export const setSession = (token: SigninResponseSuccess) => {
  localStorage.setItem(CreditasAffiliates.Token, token.accessToken);
  localStorage.setItem(CreditasAffiliates.RefreshToken, token.refreshToken);
  localStorage.setItem(
    CreditasAffiliates.ExpiresIn,
    new Date(addSeconds(Date.now(), token.expiresIn)).toISOString(),
  );
};

export const getSessionToken = (): string | null => localStorage.getItem(CreditasAffiliates.Token);

export const getDecodedSession = (): any | DecodedSession => {
  try {
    return humps.camelizeKeys(decode(getSessionToken()));
  } catch (error) {
    getLogger().error(error);
    throw new Error(ErrorMessage.DecodedSession);
  }
};

export const setStateSession = async (stateSession: StateSession) => {
  await localStorage.setItem(CreditasAffiliates.State, JSON.stringify(stateSession));
};

export const getStateSession = (): StateSession => {
  try {
    const state = JSON.parse(localStorage.getItem(CreditasAffiliates.State));
    return state;
  } catch (error) {
    getLogger().error(error);
    throw new Error(ErrorMessage.GetStateSession);
  }
};

export const getUserSession = async (): Promise<UserSession> => {
  try {
    const stateSession = await getStateSession();
    const user = stateSession?.login?.user;
    return await Promise.resolve(user);
  } catch (error) {
    throw new Error(ErrorMessage.GetUserSession);
  }
};

export const setUserSession = async (userSession: UserSession) => {
  try {
    const session = await getStateSession();
    setStateSession({
      ...session,
      login: { user: { ...userSession } },
    });
  } catch (error) {
    throw new Error(ErrorMessage.SetUserSession);
  }
};

export const deleteUserSession = async () => {
  try {
    const stateSession = await getStateSession();
    await setStateSession({ ...stateSession, login: null });
  } catch (error) {
    throw new Error(ErrorMessage.DeleteUserSession);
  }
};

export const deleteSession = async () => {
  await localStorage.removeItem(CreditasAffiliates.Token);
  await localStorage.removeItem(CreditasAffiliates.RefreshToken);
  await localStorage.removeItem(CreditasAffiliates.ExpiresIn);
  await deleteUserSession();
};
